import {
  AlertTriangle, Stethoscope, Baby, Heart, Activity,
  FileText, MessageCircle, Bell, Shield, Share2,
  BookOpen, Monitor, Wand2, TrendingUp, ClipboardList, Zap,
  Image as ImageIcon, Film, Mic, ClipboardCheck, Pill,
  TestTube, Hash, FileOutput, Apple, FileBarChart,
  GraduationCap, Presentation, Sparkles, Calculator, Newspaper,
  ShieldAlert, ScanLine, Microscope, Bot, FileCheck,
  Building2, LayoutTemplate, Calendar, Brain, Flower2, Sun, Scissors,
  Droplets, Award, UserCheck, Leaf, BookMarked, ShieldCheck, Target,
  Library, Scale, FileHeart, MessageSquare, IndianRupee, ClipboardPlus,
  Landmark, Languages, Rocket
} from 'lucide-react';

import { FLAT_TEMPLATES, TEMPLATE_COUNT } from './all_templates';

// ========================================
// GYNOVATE AI/SI - ORGANIZED MODES BY CATEGORY
// ========================================

// =============================================
// CATEGORY 1: EMERGENCY & CRITICAL CARE
// =============================================
const EMERGENCY_MODES = [
  {
    id: 'emergency',
    label: 'Emergency',
    icon: AlertTriangle,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-100',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 border-red-200',
    activeBg: 'bg-red-600',
    category: 'Emergency & Critical Care',
    desc: 'Structured 14-point emergency response protocols',
    example: '32 weeks bleeding with signs of shock',
    templates: [
      'Emergency: Postpartum hemorrhage with shock',
      'Emergency: Eclampsia with seizures',
      'Emergency: Ruptured ectopic pregnancy',
      'Emergency: Severe preeclampsia at 32 weeks',
      'Emergency: Cord prolapse during labor',
      'Emergency: Placental abruption with fetal distress',
      'Emergency: Shoulder dystocia management',
    ],
  },
];

// =============================================
// CATEGORY 2: CLINICAL CONSULTATION
// =============================================
const CLINICAL_MODES = [
  {
    id: 'opd',
    label: 'OPD Consultation',
    icon: Stethoscope,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-100',
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    activeBg: 'bg-blue-600',
    category: 'Clinical Consultation',
    desc: 'Outpatient diagnosis, investigations & EMR-ready prescriptions',
    example: 'PCOS with irregular cycles and weight gain',
    templates: [
      'OPD: PCOS with irregular cycles and hirsutism',
      'OPD: Heavy menstrual bleeding workup',
      'OPD: Post-menopausal bleeding evaluation',
      'OPD: Chronic pelvic pain differential diagnosis',
      'OPD: Recurrent vaginal discharge treatment',
      'OPD: Fibroid uterus management options',
    ],
  },
  {
    id: 'anc',
    label: 'Antenatal Care',
    icon: Baby,
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-100',
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-800 border-green-200',
    activeBg: 'bg-green-600',
    category: 'Clinical Consultation',
    desc: 'Pregnancy monitoring, risk stratification & delivery planning',
    example: '30 weeks pregnancy with hypertension',
    templates: [
      'ANC: First trimester booking visit checklist',
      'ANC: Gestational diabetes management plan',
      'ANC: High-risk pregnancy with twins',
      'ANC: Previous cesarean - VBAC counseling',
      'ANC: Rh-negative mother management',
      'ANC: Threatened preterm labor at 30 weeks',
    ],
  },
  {
    id: 'ultrasound',
    label: 'Ultrasound',
    icon: Monitor,
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
    border: 'border-cyan-100',
    dot: 'bg-cyan-500',
    badge: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    activeBg: 'bg-cyan-600',
    category: 'Clinical Consultation',
    desc: 'Imaging interpretation, benign vs malignant, reporting',
    example: 'Ovarian cyst 6cm - characterization',
    templates: [
      'Ultrasound: Ovarian cyst - benign vs suspicious features',
      'Ultrasound: Fibroid mapping for surgery planning',
      'Ultrasound: First trimester viability scan report',
      'Ultrasound: Anomaly scan 20 weeks checklist',
      'Ultrasound: Growth scan with Doppler interpretation',
      'Ultrasound: Endometrial thickness interpretation',
    ],
  },
  {
    id: 'counselling',
    label: 'Counselling',
    icon: MessageCircle,
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-100',
    dot: 'bg-yellow-500',
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    activeBg: 'bg-yellow-600',
    category: 'Clinical Consultation',
    desc: 'Doctor, nurse & patient-friendly counselling versions',
    example: 'Counselling for hysterectomy options',
    templates: [
      'Counselling: Hysterectomy options - pros and cons',
      'Counselling: PCOS long-term management',
      'Counselling: Infertility treatment options',
      'Counselling: Menopause HRT benefits and risks',
      'Counselling: High-risk pregnancy expectations',
      'Counselling: Cancer diagnosis - breaking bad news',
    ],
  },
  {
    id: 'followup',
    label: 'Follow-up Message',
    icon: Bell,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-100',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    activeBg: 'bg-amber-600',
    category: 'Clinical Consultation',
    desc: 'WhatsApp-ready patient follow-up messages',
    example: 'Post C-section day 5 recovery check',
    templates: [
      'Follow-up: Post surgery day 3 recovery check',
      'Follow-up: Medication compliance reminder',
      'Follow-up: Lab report discussion appointment',
      'Follow-up: Post IVF transfer care instructions',
      'Follow-up: Antenatal visit reminder',
    ],
  },
];

// =============================================
// CATEGORY 3: SPECIALTY AREAS
// =============================================
const SPECIALTY_MODES = [
  {
    id: 'infertility',
    label: 'Infertility',
    icon: Heart,
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-100',
    dot: 'bg-purple-500',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    activeBg: 'bg-purple-600',
    category: 'Specialty Areas',
    desc: 'Complete workup & treatment ladder from lifestyle to IVF',
    example: 'Primary infertility 2 years, failed IUI cycles',
    templates: [
      'Infertility: Primary infertility 3 years basic workup',
      'Infertility: Secondary infertility after C-section',
      'Infertility: Male factor with low sperm count',
      'Infertility: Failed 2 IUI cycles - next steps',
      'Infertility: Low AMH treatment options',
      'Infertility: PCOS with anovulation - ovulation induction',
    ],
  },
  {
    id: 'menopause-ai',
    label: 'Menopause AI',
    icon: Sun,
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-100',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    activeBg: 'bg-orange-600',
    category: 'Specialty Areas',
    desc: 'Symptoms, HRT, lifestyle & counseling',
    example: 'Hot flashes management without hormones',
    templates: [
      'Perimenopause symptoms checklist and management',
      'Hot flashes - hormonal and non-hormonal treatments',
      'Vaginal dryness and GSM treatment options',
      'HRT candidacy assessment - risks and benefits',
      'Osteoporosis prevention in menopause',
      'Managing menopause in breast cancer survivors',
    ],
  },
  {
    id: 'cancers-gynec',
    label: 'Gynec Cancers',
    icon: Target,
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-100',
    dot: 'bg-rose-500',
    badge: 'bg-rose-100 text-rose-800 border-rose-200',
    activeBg: 'bg-rose-600',
    category: 'Specialty Areas',
    desc: 'Screening, staging, treatment & follow-up',
    example: 'Cervical cancer staging and treatment',
    templates: [
      'Cervical cancer - screening, diagnosis, FIGO staging',
      'Ovarian cancer - CA-125, imaging, staging surgery',
      'Endometrial cancer - risk factors, workup, treatment',
      'Vulvar cancer - diagnosis and management',
      'Gestational trophoblastic disease - molar pregnancy',
      'Cancer in pregnancy - management principles',
      'Chemotherapy regimens in gynec cancers',
      'Genetic counseling BRCA mutations',
    ],
  },
  {
    id: 'preventive-gynec',
    label: 'Preventive Gynecology',
    icon: ShieldCheck,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-100',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    activeBg: 'bg-emerald-600',
    category: 'Specialty Areas',
    desc: 'Screening, vaccination & early detection',
    example: 'Annual gynec checkup protocol',
    templates: [
      'Annual gynecologic examination checklist',
      'Cervical cancer screening - Pap smear guidelines',
      'HPV vaccination - age, doses, counseling',
      'Breast cancer screening - mammography guidelines',
      'Ovarian cancer screening - high risk patients',
      'Preconception counseling checklist',
      'Adolescent gynecology - first visit guide',
      'STI screening protocols',
    ],
  },
  {
    id: 'cosmetic-gynec',
    label: 'Cosmetic Gynecology',
    icon: Scissors,
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    border: 'border-pink-100',
    dot: 'bg-pink-500',
    badge: 'bg-pink-100 text-pink-800 border-pink-200',
    activeBg: 'bg-pink-600',
    category: 'Specialty Areas',
    desc: 'Aesthetic procedures & patient selection',
    example: 'Labiaplasty patient counseling',
    templates: [
      'Cosmetic gynecology - overview of procedures',
      'Labiaplasty - indications and patient selection',
      'Vaginoplasty - vaginal tightening procedures',
      'Vaginal rejuvenation with laser',
      'Non-surgical vaginal rejuvenation options',
      'Medico-legal aspects of cosmetic gynecology',
    ],
  },
  {
    id: 'lactation-assist',
    label: 'Lactation Assist',
    icon: Droplets,
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-100',
    dot: 'bg-sky-500',
    badge: 'bg-sky-100 text-sky-800 border-sky-200',
    activeBg: 'bg-sky-600',
    category: 'Specialty Areas',
    desc: 'Breastfeeding support & troubleshooting',
    example: 'How to increase breast milk supply',
    templates: [
      'Breastfeeding basics - first hour [YouTube: Breastfeeding Latch]',
      'Correct latching technique [YouTube: Nursing Positions]',
      'Breast engorgement - prevention and management',
      'Low milk supply - causes and solutions',
      'Foods to increase breast milk - galactagogues',
      'Mastitis diagnosis and management',
      'Pumping and storing breast milk guide',
    ],
  },
];

// =============================================
// CATEGORY 4: SURGERY
// =============================================
const SURGERY_MODES = [
  {
    id: 'discharge',
    label: 'Discharge Summary',
    icon: FileText,
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    border: 'border-slate-100',
    dot: 'bg-slate-500',
    badge: 'bg-slate-100 text-slate-800 border-slate-200',
    activeBg: 'bg-slate-600',
    category: 'Surgery & Documentation',
    desc: 'NABH-compliant discharge documentation',
    example: 'Post laparoscopic hysterectomy day 2',
    templates: [
      'Discharge: Post laparoscopic hysterectomy',
      'Discharge: Post cesarean section day 3',
      'Discharge: Post hysteroscopy and D&C',
      'Discharge: Post laparoscopic myomectomy',
      'Discharge: Post vaginal delivery',
    ],
  },
  {
    id: 'consent',
    label: 'Consent Format',
    icon: Shield,
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-100',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    activeBg: 'bg-indigo-600',
    category: 'Surgery & Documentation',
    desc: 'NABH-compliant medico-legal consent forms',
    example: 'Consent for laparoscopic hysterectomy',
    templates: [
      'Consent: Cesarean section comprehensive',
      'Consent: Laparoscopic hysterectomy detailed',
      'Consent: Hysteroscopy diagnostic and operative',
      'Consent: Laparoscopic myomectomy',
      'Consent: IVF/ICSI treatment consent',
    ],
  },
  {
    id: 'surgical-procedures',
    label: 'Surgical Procedures',
    icon: ClipboardCheck,
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-100',
    dot: 'bg-violet-500',
    badge: 'bg-violet-100 text-violet-800 border-violet-200',
    activeBg: 'bg-violet-600',
    category: 'Surgery & Documentation',
    desc: 'Step-by-step surgical techniques & procedures',
    example: 'Laparoscopic myomectomy technique',
    templates: [
      'Procedure: Total laparoscopic hysterectomy - step by step',
      'Procedure: Laparoscopic myomectomy technique',
      'Procedure: Diagnostic laparoscopy steps',
      'Procedure: Hysteroscopic polypectomy technique',
      'Procedure: Cesarean section - surgical steps',
      'Procedure: Laparoscopic salpingectomy for ectopic',
      'Procedure: Vaginal hysterectomy technique',
      'Procedure: Abdominal myomectomy steps',
      'Procedure: LEEP/LLETZ procedure technique',
      'Procedure: D&C - dilation and curettage steps',
      'Procedure: Cervical cerclage insertion',
      'Procedure: Manual removal of placenta',
    ],
  },
  {
    id: 'medicolegal-help',
    label: 'Medicolegal Help',
    icon: Scale,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-100',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 border-red-200',
    activeBg: 'bg-red-600',
    category: 'Surgery & Documentation',
    desc: 'Indian laws for OBGYN - MTP, PCPNDT, POCSO & more',
    example: 'MTP Act 2021 - gestational limits and conditions',
    templates: [
      // MTP Act
      'MTP Act 2021 - Complete overview and key changes',
      'MTP Act - Gestational age limits (20 vs 24 weeks)',
      'MTP Act - Who can perform MTP and where',
      'MTP Act - Consent requirements (minor, married, unmarried)',
      'MTP Act - Special categories for 24 weeks termination',
      'MTP Act - Medical Board composition and role',
      'MTP Act - Documentation and Form C requirements',
      'MTP Act - Confidentiality and privacy provisions',
      'MTP Act - Penalties for violations',
      // PCPNDT Act
      'PCPNDT Act 1994 - Complete overview',
      'PCPNDT - Prohibited acts and penalties',
      'PCPNDT - Registration of genetic clinics',
      'PCPNDT - Form F - Declaration before ultrasound',
      'PCPNDT - Record keeping requirements (3 years)',
      'PCPNDT - Appropriate Authority and complaints',
      'PCPNDT - Cognizable, non-bailable offenses',
      'PCPNDT - Punishment: imprisonment and fines',
      'PCPNDT - Sting operations and legal implications',
      // POCSO Act
      'POCSO Act 2012 - Relevance in gynecology',
      'POCSO - Mandatory reporting by doctors',
      'POCSO - Age of consent (18 years) implications',
      'POCSO - Examination of child victims protocol',
      'POCSO - Medical examination within 24 hours',
      'POCSO - Documentation and evidence collection',
      // Consumer Protection Act
      'Consumer Protection Act 2019 - Medical negligence',
      'CPA - Definition of deficiency in service',
      'CPA - Compensation limits by forum (District/State/National)',
      'CPA - Limitation period for filing complaints',
      'CPA - Defenses available to doctors',
      // IPC Sections
      'IPC Section 312 - Causing miscarriage',
      'IPC Section 313 - Miscarriage without consent',
      'IPC Section 314 - Death caused by act done to cause miscarriage',
      'IPC Section 315 - Act to prevent child being born alive',
      'IPC Section 316 - Causing death of unborn child',
      'IPC Section 319-326 - Hurt and grievous hurt',
      'IPC Section 304A - Death by negligence',
      'IPC Section 80 - Accident in doing lawful act',
      // Surrogacy & ART Acts
      'Surrogacy Regulation Act 2021 - Key provisions',
      'Surrogacy Act - Who can be surrogate mother',
      'Surrogacy Act - Eligibility of intending couple',
      'Surrogacy Act - Altruistic vs commercial surrogacy',
      'Surrogacy Act - Insurance and compensation',
      'ART Regulation Act 2021 - Overview',
      'ART Act - Registration of ART clinics',
      'ART Act - Gamete donor regulations',
      'ART Act - Rights of children born through ART',
      // Other Relevant Laws
      'Indian Medical Council Regulations 2002 - Ethics',
      'Clinical Establishments Act 2010 - Registration',
      'Drugs and Cosmetics Act - Relevant provisions',
      'Transplantation of Human Organs Act - Relevance',
      'Mental Healthcare Act 2017 - Pregnancy provisions',
      'Rights of Persons with Disabilities Act 2016',
      // Documentation & Certificates
      'Death certificate - Format and requirements',
      'Stillbirth certificate - Legal requirements',
      'Birth certificate - Hospital responsibilities',
      'Fitness certificate - Medicolegal implications',
      'Medical certificate - Dos and donts',
      'Case sheet documentation - Legal standards',
      // Medicolegal Situations
      'Handling police cases - Rape, assault',
      'Age determination - Legal requirements',
      'Sexual assault examination - Guidelines',
      'Domestic violence - Doctor responsibilities',
      'Medical negligence - How to avoid',
      'Informed consent - Legal requirements',
      'Refusal of treatment - Documentation',
      'Against medical advice discharge - Protection',
      'Second opinion - When and how',
      'Expert witness - Court testimony tips',
    ],
  },
];

// =============================================
// CATEGORY 5: EDUCATION & RESOURCES
// =============================================
const EDUCATION_MODES = [
  {
    id: 'anatomy',
    label: 'Know the Anatomy',
    icon: BookMarked,
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-100',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    activeBg: 'bg-indigo-600',
    category: 'Education & Resources',
    desc: 'Detailed anatomy for surgery & patient education',
    example: 'Pelvic floor anatomy for prolapse surgery',
    templates: [
      'Anatomy: Female reproductive system overview',
      'Anatomy: Uterus - layers, blood supply, lymphatics',
      'Anatomy: Ovary and fallopian tube relations',
      'Anatomy: Pelvic floor muscles and supports',
      'Anatomy: Ureter course in gynec surgery',
      'Anatomy: Blood supply to uterus and adnexa',
      'Anatomy: Lymphatic drainage - surgical nodes',
      'Anatomy: Vaginal anatomy and supports',
      'Anatomy: Vulvar anatomy - surgical landmarks',
      'Anatomy: Pelvic spaces - avascular planes',
      'Anatomy: Bony pelvis - obstetric diameters',
    ],
  },
  {
    id: 'guidelines',
    label: 'Latest Guidelines',
    icon: BookOpen,
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-100',
    dot: 'bg-teal-500',
    badge: 'bg-teal-100 text-teal-800 border-teal-200',
    activeBg: 'bg-teal-600',
    category: 'Education & Resources',
    desc: 'ACOG, RCOG, FOGSI, WHO guidelines',
    example: 'ACOG guidelines for GDM management',
    templates: [
      'Guidelines: ACOG GDM screening 2024',
      'Guidelines: RCOG preterm labor management',
      'Guidelines: FOGSI PCOS management 2024',
      'Guidelines: WHO antenatal care recommendations',
      'Guidelines: FIGO staging gynec cancers',
      'Guidelines: ESHRE endometriosis management',
    ],
  },
  {
    id: 'drug-interactions',
    label: 'Drug Interactions',
    icon: Pill,
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-100',
    dot: 'bg-red-400',
    badge: 'bg-red-100 text-red-700 border-red-200',
    activeBg: 'bg-red-500',
    category: 'Education & Resources',
    desc: 'Pregnancy safety & drug interactions',
    example: 'Antihypertensives safe in pregnancy',
    templates: [
      'Drug safety: Antibiotics safe in pregnancy by trimester',
      'Drug safety: Antihypertensives in pregnancy',
      'Drug safety: Analgesics safe during breastfeeding',
      'Drug interaction: Metformin with other medications',
      'Drug safety: Teratogenic drugs to avoid',
      'Drug safety: Psychiatric medications in pregnancy',
    ],
  },
  {
    id: 'ebooks-library',
    label: 'E-Books Library',
    icon: Library,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-100',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    activeBg: 'bg-amber-600',
    category: 'Education & Resources',
    desc: 'Free gynecology e-books & PDF resources',
    example: 'Williams Obstetrics key chapters',
    templates: [
      'E-Book: Williams Obstetrics - key topics summary',
      'E-Book: Te Lindes Operative Gynecology - techniques',
      'E-Book: Berek and Novak Gynecology - essentials',
      'E-Book: Clinical Gynecologic Endocrinology - PCOS chapter',
      'E-Book: High Risk Pregnancy Management - protocols',
      'E-Book: Practical Guide to Laparoscopy - step by step',
      'E-Book: Ultrasound in Obstetrics - image interpretation',
      'E-Book: Infertility Management - IVF protocols',
      'E-Book: WHO Reproductive Health Guidelines - PDF',
      'E-Book: FOGSI Good Clinical Practice - recommendations',
      'E-Book: RCOG Green Top Guidelines - collection',
      'E-Book: ACOG Practice Bulletins - key summaries',
      'PDF Resource: Partograph interpretation guide',
      'PDF Resource: Bishop score and labor induction',
      'PDF Resource: Emergency obstetric care manual',
    ],
  },
  {
    id: 'papers-posters',
    label: 'Papers & Posters',
    icon: GraduationCap,
    bg: 'bg-fuchsia-50',
    text: 'text-fuchsia-700',
    border: 'border-fuchsia-100',
    dot: 'bg-fuchsia-500',
    badge: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
    activeBg: 'bg-fuchsia-600',
    category: 'Education & Resources',
    desc: 'Research papers, case reports & poster formats',
    example: 'Case report format for rare presentation',
    templates: [
      'Research: Case report structure and format',
      'Research: Poster presentation template',
      'Research: Abstract writing for conference',
      'Research: Literature review methodology',
      'Research: Statistical analysis for OBGYN studies',
    ],
  },
  {
    id: 'presentation',
    label: 'Presentation Maker',
    icon: Presentation,
    bg: 'bg-lime-50',
    text: 'text-lime-700',
    border: 'border-lime-100',
    dot: 'bg-lime-500',
    badge: 'bg-lime-100 text-lime-800 border-lime-200',
    activeBg: 'bg-lime-600',
    category: 'Education & Resources',
    desc: 'CME slides with speaker notes',
    example: 'PCOS management CME presentation',
    templates: [
      'Presentation: PCOS management update CME',
      'Presentation: High risk pregnancy overview',
      'Presentation: Laparoscopy basics for residents',
      'Presentation: Infertility workup for GPs',
      'Presentation: Cervical cancer screening update',
    ],
  },
  {
    id: 'exam-revision-pg',
    label: 'PG Exam Revision',
    icon: Brain,
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-100',
    dot: 'bg-rose-500',
    badge: 'bg-rose-100 text-rose-800 border-rose-200',
    activeBg: 'bg-rose-600',
    category: 'Education & Resources',
    desc: 'Last-minute revision for PG exams (MS/MD/DNB)',
    example: 'Quick revision: High-risk pregnancy topics',
    templates: [
      'Exam Revision: Bishop score and labor induction - quick facts',
      'Exam Revision: Partograph interpretation - must know points',
      'Exam Revision: Preeclampsia management protocol - stepwise',
      'Exam Revision: Antepartum hemorrhage - causes and management',
      'Exam Revision: Postpartum hemorrhage - active management',
      'Exam Revision: PCOS diagnostic criteria - Rotterdam vs NIH',
      'Exam Revision: Endometriosis staging - rAFS classification',
      'Exam Revision: Cervical cancer staging - FIGO 2018',
      'Exam Revision: Ovarian tumors - WHO classification quick review',
      'Exam Revision: Contraception - mechanism and failure rates',
      'Exam Revision: Infertility workup - day-wise protocol',
      'Exam Revision: Abnormal uterine bleeding - PALM-COEIN',
      'Exam Revision: Gestational trophoblastic disease - staging',
      'Exam Revision: Instrumental delivery - indications & technique',
      'Exam Revision: Cesarean section - indications by priority',
      'Exam Revision: Fetal monitoring - CTG interpretation pearls',
      'Exam Revision: Drugs in pregnancy - FDA categories',
      'Exam Revision: Ectopic pregnancy - management algorithm',
      'Exam Revision: Multiple pregnancy - complications to remember',
      'Exam Revision: Preterm labor - tocolytics comparison',
    ],
  },
  {
    id: 'obgyn-conferences-india',
    label: 'OBGYN Conferences India',
    icon: Calendar,
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-100',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    activeBg: 'bg-orange-600',
    category: 'Education & Resources',
    desc: 'Upcoming OBGYN conferences & events in India',
    example: 'FOGSI conference dates and registration',
    templates: [
      'Conference: AICOG (All India Congress of Obstetrics & Gynecology) - details',
      'Conference: FOGSI annual conference - dates and venue',
      'Conference: ICOG national conference - registration info',
      'Conference: State FOGSI conferences - Maharashtra, Karnataka, Tamil Nadu',
      'Conference: IAGE (Endoscopy Society) annual meet - highlights',
      'Conference: IFS (Indian Fertility Society) conference - topics',
      'Conference: ISOUG (Ultrasound Society) workshop - hands-on',
      'Conference: AICC-RCOG (India-UK collaboration) - updates',
      'Conference: NARCHI (Reproductive Health) conference - schedule',
      'Conference: Maternal Fetal Medicine conference India - speakers',
      'Conference: Gynec Oncology Society India - annual meet',
      'Conference: AOGD (Delhi OBGYN) conference - CME credits',
      'Conference: OGSB (Bengal) annual meeting - registration',
      'Conference: KOGS (Karnataka) state conference - agenda',
      'Conference: MOGS (Maharashtra) annual conference - highlights',
      'Conference: TOGS (Tamil Nadu) conference - abstract submission',
      'Workshop: FOGSI hands-on laparoscopy workshops - 2024-25',
      'Workshop: Hysteroscopy training programs India - schedule',
      'Workshop: Urogynaecology workshops - available courses',
      'Workshop: Fetal medicine certification courses - India',
    ],
  },
  {
    id: 'mrcog-preparation',
    label: 'Prepare for MRCOG',
    icon: Award,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-100',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    activeBg: 'bg-emerald-600',
    category: 'Education & Resources',
    desc: 'MRCOG Part 1, 2, 3 exam preparation resources',
    example: 'MRCOG Part 2 SBA practice questions',
    templates: [
      'MRCOG Part 1: Anatomy high-yield topics for exam',
      'MRCOG Part 1: Biochemistry must-know pathways',
      'MRCOG Part 1: Physiology of pregnancy - key concepts',
      'MRCOG Part 1: Embryology important points',
      'MRCOG Part 1: Pharmacology - drugs in pregnancy',
      'MRCOG Part 2: SBA practice questions - obstetrics',
      'MRCOG Part 2: SBA practice questions - gynaecology',
      'MRCOG Part 2: EMQ practice - high-risk pregnancy',
      'MRCOG Part 2: EMQ practice - gynae emergencies',
      'MRCOG Part 2: Clinical governance questions',
      'MRCOG Part 2: RCOG guidelines summary for exam',
      'MRCOG Part 3: OSCE stations - history taking tips',
      'MRCOG Part 3: OSCE stations - communication skills',
      'MRCOG Part 3: OSCE stations - clinical management',
      'MRCOG Part 3: OSCE stations - breaking bad news',
      'MRCOG Part 3: Teaching station preparation',
      'MRCOG: Study plan for 6 months preparation',
      'MRCOG: Recommended textbooks and resources',
      'MRCOG: Online question banks comparison',
      'MRCOG: Common mistakes to avoid in exam',
      'MRCOG: Green Top Guidelines - exam-relevant summaries',
      'MRCOG: TOG (The Obstetrician & Gynaecologist) key articles',
    ],
  },
];

// =============================================
// CATEGORY 6: WELLNESS & LIFESTYLE
// =============================================
const WELLNESS_MODES = [
  {
    id: 'garbhasanskar',
    label: 'Garbhasanskar',
    icon: Leaf,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-100',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    activeBg: 'bg-amber-600',
    category: 'Wellness & Lifestyle',
    desc: 'Indian prenatal practices & mantras',
    example: 'Garbhasanskar mantras for each trimester',
    templates: [
      'Garbhasanskar - Introduction and scientific basis',
      'First trimester Garbhasanskar practices',
      'Mantras for pregnancy - Gayatri, Santana Gopala [YouTube: Pregnancy Mantras]',
      'Garbha Raksha Stotram with meaning [YouTube: Garbha Raksha]',
      'Classical music for fetal brain development [YouTube: Mozart Pregnancy]',
      'Ragas for pregnancy [YouTube: Pregnancy Ragas]',
      'Ayurvedic diet principles during pregnancy',
      'Month-wise Garbhasanskar activities (1-9 months)',
    ],
  },
  {
    id: 'meditation-yoga',
    label: 'Yoga & Meditation',
    icon: Flower2,
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    border: 'border-teal-100',
    dot: 'bg-teal-500',
    badge: 'bg-teal-100 text-teal-800 border-teal-200',
    activeBg: 'bg-teal-600',
    category: 'Wellness & Lifestyle',
    desc: 'Yoga & meditation with YouTube links',
    example: 'Yoga poses safe during pregnancy',
    templates: [
      'Prenatal yoga for first trimester [YouTube: Pregnancy Yoga]',
      'Third trimester gentle yoga [YouTube: Late Pregnancy Yoga]',
      'Postpartum yoga for recovery [YouTube: Postnatal Yoga]',
      'Yoga for PCOS - hormone balancing [YouTube: PCOS Yoga]',
      'Meditation for fertility [YouTube: Fertility Meditation]',
      'Pelvic floor yoga exercises [YouTube: Kegel Yoga]',
      'Yoga for menopausal symptoms [YouTube: Menopause Yoga]',
      'Breathing exercises for labor [YouTube: Birth Breathing]',
    ],
  },
  {
    id: 'diet-plans',
    label: 'Diet Plans',
    icon: Apple,
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-100',
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-800 border-green-200',
    activeBg: 'bg-green-600',
    category: 'Wellness & Lifestyle',
    desc: 'Evidence-based nutrition for womens health',
    example: 'PCOS diet plan with meal suggestions',
    templates: [
      'Diet: PCOS low glycemic index plan',
      'Diet: Pregnancy trimester-wise nutrition',
      'Diet: Iron rich foods for anemia',
      'Diet: GDM carb counting guide',
      'Diet: Fertility boosting foods',
      'Diet: Menopause bone health diet',
      'Diet: Post-operative recovery nutrition',
    ],
  },
  {
    id: 'mental-health-docs',
    label: 'Talk to Gia',
    icon: Brain,
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-100',
    dot: 'bg-purple-500',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    activeBg: 'bg-purple-600',
    category: 'Wellness & Lifestyle',
    desc: 'Confidential mental health coach for gynaecologists — stress, burnout, anxiety',
    example: 'I had a stillbirth today and I cannot stop thinking about it',
    templates: [
      "I had a difficult outcome today — I just need to talk",
      "I am feeling burned out and exhausted",
      "I cannot sleep after a tough on-call night",
      "A patient complaint is weighing on me",
      "I am anxious before a big surgery tomorrow",
      "How do I cope after an adverse obstetric outcome?",
      "I am struggling with work-life balance — help me plan",
      "I feel imposter syndrome despite years of practice",
      "Give me a 5-minute guided breathing exercise",
      "Signs I should speak to a professional therapist",
    ],
  },
];

// =============================================
// CATEGORY 7: PRACTICE MANAGEMENT & MARKETING
// =============================================
const PRACTICE_MODES = [
  {
    id: 'social-media',
    label: 'Social Media',
    icon: Share2,
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    border: 'border-pink-100',
    dot: 'bg-pink-500',
    badge: 'bg-pink-100 text-pink-800 border-pink-200',
    activeBg: 'bg-pink-600',
    category: 'Practice Management',
    desc: 'Instagram, Reels, captions & hashtags',
    example: 'Instagram reel script for PCOS awareness',
    templates: [
      'Social Media: PCOS awareness Instagram post',
      'Social Media: Pregnancy tips carousel',
      'Social Media: Menopause myths busted reel',
      'Social Media: Fertility awareness post',
      'Social Media: Doctor introduction reel script',
    ],
  },
  {
    id: 'social-scheduler',
    label: 'AI Social Scheduler',
    icon: Calendar,
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-100',
    dot: 'bg-violet-500',
    badge: 'bg-violet-100 text-violet-800 border-violet-200',
    activeBg: 'bg-violet-600',
    category: 'Practice Management',
    desc: 'Content calendar & posting schedules',
    example: 'Monthly content calendar for clinic',
    templates: [
      'Create weekly social media content calendar',
      'Monthly Instagram posting schedule',
      'Best times to post healthcare content',
      'Content batching strategy for busy doctors',
      'Health awareness days calendar for OBGYN',
    ],
  },
  {
    id: 'brand-building',
    label: 'Brand Building',
    icon: Award,
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-100',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    activeBg: 'bg-indigo-600',
    category: 'Practice Management',
    desc: 'Personal branding for doctors',
    example: 'Building authority in subspecialty',
    templates: [
      'Personal branding for doctors - getting started',
      'LinkedIn optimization for medical professionals',
      'Building authority in your subspecialty',
      'Google My Business optimization for clinics',
      'Online reputation management for doctors',
      'Website design for medical practices',
    ],
  },
  {
    id: 'personality-skills',
    label: 'Personality Skills',
    icon: UserCheck,
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-100',
    dot: 'bg-rose-500',
    badge: 'bg-rose-100 text-rose-800 border-rose-200',
    activeBg: 'bg-rose-600',
    category: 'Practice Management',
    desc: 'Communication & leadership for doctors',
    example: 'How to communicate bad news to patients',
    templates: [
      'Effective patient communication skills',
      'Breaking bad news - SPIKES protocol',
      'Managing angry or difficult patients',
      'Leadership skills for department heads',
      'Public speaking for medical conferences',
      'Emotional intelligence in medicine',
    ],
  },
  {
    id: 'article-blog',
    label: 'Article / Blog',
    icon: FileBarChart,
    bg: 'bg-cyan-50',
    text: 'text-cyan-700',
    border: 'border-cyan-100',
    dot: 'bg-cyan-500',
    badge: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    activeBg: 'bg-cyan-600',
    category: 'Practice Management',
    desc: 'SEO-optimised medical articles',
    example: 'PCOS blog post for patient education',
    templates: [
      'Blog: PCOS - causes, symptoms, treatment',
      'Article: Pregnancy care tips for new mothers',
      'Blog: Understanding menopause changes',
      'Article: Infertility - when to see a specialist',
      'Blog: Cervical cancer prevention - HPV vaccine',
    ],
  },
  {
    id: 'practice-growth',
    label: 'Practice Growth',
    icon: TrendingUp,
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-100',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    activeBg: 'bg-emerald-600',
    category: 'Practice Management',
    desc: 'Clinic branding & growth strategies',
    example: 'Marketing strategy for new clinic',
    templates: [
      'Practice Growth: Google reviews strategy',
      'Practice Growth: Patient referral program',
      'Practice Growth: Corporate health camps',
      'Practice Growth: Insurance tie-ups guide',
      'Practice Growth: Clinic SOPs for quality',
    ],
  },
  {
    id: 'design-opd',
    label: 'Design Your OPD',
    icon: Building2,
    bg: 'bg-stone-50',
    text: 'text-stone-700',
    border: 'border-stone-100',
    dot: 'bg-stone-500',
    badge: 'bg-stone-100 text-stone-800 border-stone-200',
    activeBg: 'bg-stone-600',
    category: 'Practice Management',
    desc: 'AI architect for clinic & hospital design',
    example: 'Layout plan for gynec OPD',
    templates: [
      'Design: Gynec OPD layout - patient flow',
      'Design: Labor room setup requirements',
      'Design: IVF center planning essentials',
      'Design: Operation theater specifications',
      'Design: Waiting area patient comfort',
    ],
  },
  {
    id: 'organize-cme',
    label: 'Organize CME',
    icon: GraduationCap,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-100',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    activeBg: 'bg-amber-600',
    category: 'Practice Management',
    desc: 'Plan & execute CME programs with credit points',
    example: 'Organize CME on laparoscopic surgery',
    templates: [
      'CME: Plan a half-day workshop on PCOS management',
      'CME: Create agenda for hands-on laparoscopy training',
      'CME: Draft invitation letter for CME faculty',
      'CME: Certificate template with credit points',
      'CME: Budget planning for medical workshop',
      'CME: Sponsorship proposal for pharma companies',
      'CME: Post-CME feedback form questions',
      'CME: Credit point application to medical council',
      'CME: Online CME platform setup guide',
      'CME: CME attendance and registration system',
      'CME: Faculty honorarium guidelines',
      'CME: CME marketing and promotion strategy',
    ],
  },
  {
    id: 'organize-conference',
    label: 'Organize Conference',
    icon: Presentation,
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-100',
    dot: 'bg-purple-500',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    activeBg: 'bg-purple-600',
    category: 'Practice Management',
    desc: 'Full conference planning from A to Z',
    example: 'Organize state-level FOGSI conference',
    templates: [
      'Conference: Create scientific program for 2-day event',
      'Conference: Draft call for abstracts announcement',
      'Conference: Sponsorship tier packages for industry',
      'Conference: Venue selection checklist',
      'Conference: Speaker invitation letter template',
      'Conference: Registration fee structure planning',
      'Conference: Conference website content outline',
      'Conference: Abstract submission guidelines',
      'Conference: Judge panel for paper presentations',
      'Conference: Award categories and criteria',
      'Conference: Delegate kit contents checklist',
      'Conference: Post-conference report template',
      'Conference: Budget breakdown for 500+ attendees',
      'Conference: Live workshop setup requirements',
      'Conference: Virtual/hybrid conference tech setup',
      'Conference: Social event and networking session ideas',
    ],
  },
];

// =============================================
// CATEGORY 8: INDIA SPECIFIC - FOGSI, DRUGS, GOVT
// =============================================
const INDIA_MODES = [
  {
    id: 'fogsi-icog',
    label: 'FOGSI Guidelines',
    icon: FileHeart,
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-100',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    activeBg: 'bg-orange-600',
    category: 'India Specific',
    desc: 'FOGSI GCPRs, ICOG recommendations & state protocols',
    example: 'FOGSI guidelines for PCOS management',
    templates: [
      // FOGSI GCPRs
      'FOGSI GCPR: PCOS Diagnosis and Management 2024',
      'FOGSI GCPR: Gestational Diabetes Mellitus',
      'FOGSI GCPR: Hypertensive Disorders in Pregnancy',
      'FOGSI GCPR: Prevention of Preterm Birth',
      'FOGSI GCPR: Anemia in Pregnancy',
      'FOGSI GCPR: Recurrent Pregnancy Loss',
      'FOGSI GCPR: Ectopic Pregnancy Management',
      'FOGSI GCPR: Abnormal Uterine Bleeding',
      'FOGSI GCPR: Endometriosis Management',
      'FOGSI GCPR: Cervical Cancer Screening',
      'FOGSI GCPR: Contraception Guidelines',
      'FOGSI GCPR: Infertility Management',
      'FOGSI GCPR: Menopause HRT Guidelines',
      'FOGSI GCPR: Safe Abortion Guidelines',
      // ICOG Recommendations
      'ICOG: Ovulation Induction Protocols',
      'ICOG: IVF Standard Operating Procedures',
      'ICOG: Donor Gamete Guidelines',
      'ICOG: Surrogacy Medical Protocol',
      'ICOG: Fertility Preservation Guidelines',
      'ICOG: Male Infertility Workup',
      'ICOG: Poor Ovarian Response Management',
      // State Protocols
      'Maharashtra: OBGYN Emergency Protocol',
      'Tamil Nadu: Maternal Health Standards',
      'Karnataka: High Risk Pregnancy Guidelines',
      'Kerala: Quality Standards in OBGYN',
      'Delhi: Tertiary Care Referral Guidelines',
    ],
  },
  {
    id: 'indian-drugs',
    label: 'Indian Drug Database',
    icon: Pill,
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-100',
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-800 border-green-200',
    activeBg: 'bg-green-600',
    category: 'India Specific',
    desc: 'Generic to brand, prices & pregnancy safety',
    example: 'Metformin Indian brands and prices',
    templates: [
      // Generic to Brand
      'Drug: Metformin → Glycomet, Glucophage, Obimet prices',
      'Drug: Letrozole → Letoval, Femara, Letroz prices',
      'Drug: Clomiphene → Siphene, Clofert, Fertyl prices',
      'Drug: Progesterone → Susten, Gestone, Naturogest prices',
      'Drug: Folic Acid → Folvite, Folicare prices',
      'Drug: Iron + Folic Acid → Autrin, Dexorange, Livogen',
      'Drug: Calcium → Shelcal, Calcimax, Gemcal prices',
      'Drug: Misoprostol → Misoprost, Cytolog prices',
      'Drug: Tranexamic Acid → Pause, Tranexa prices',
      'Drug: Mefenamic Acid → Meftal, Ponstan prices',
      'Drug: Cabergoline → Cabgolin, Dostinex prices',
      'Drug: GnRH Agonist → Lupride, Zoladex prices',
      // Pregnancy Safety
      'Pregnancy Safety: Category A drugs (safest)',
      'Pregnancy Safety: Category B drugs list',
      'Pregnancy Safety: Category X drugs (avoid)',
      'Pregnancy Safety: Antibiotics safe by trimester',
      'Pregnancy Safety: Antihypertensives comparison',
      'Breastfeeding Safety: Compatible drugs list',
    ],
  },
  {
    id: 'whatsapp-templates',
    label: 'WhatsApp Templates',
    icon: MessageSquare,
    bg: 'bg-green-50',
    text: 'text-green-600',
    border: 'border-green-100',
    dot: 'bg-green-400',
    badge: 'bg-green-100 text-green-700 border-green-200',
    activeBg: 'bg-green-500',
    category: 'India Specific',
    desc: 'Ready Hindi/English patient messages',
    example: 'Post-op care message in Hindi',
    templates: [
      // Hindi Templates
      'WhatsApp Hindi: Appointment reminder tomorrow',
      'WhatsApp Hindi: Post cesarean care day 1-3',
      'WhatsApp Hindi: Post laparoscopy care',
      'WhatsApp Hindi: First trimester dos and donts',
      'WhatsApp Hindi: Labor signs when to come',
      'WhatsApp Hindi: Breastfeeding tips newborn',
      'WhatsApp Hindi: Medicine reminder message',
      'WhatsApp Hindi: Follow-up visit reminder',
      'WhatsApp Hindi: Lab report ready notification',
      'WhatsApp Hindi: Normal report reassurance',
      // English Templates
      'WhatsApp English: Appointment confirmation',
      'WhatsApp English: Post surgery care',
      'WhatsApp English: Pregnancy care tips',
      // Regional Languages
      'WhatsApp Marathi: Post-op care instructions',
      'WhatsApp Tamil: Pregnancy care message',
      'WhatsApp Telugu: Appointment reminder',
      'WhatsApp Gujarati: Medicine instructions',
      'WhatsApp Bengali: Follow-up reminder',
    ],
  },
  {
    id: 'insurance-tpa',
    label: 'Insurance & TPA',
    icon: IndianRupee,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-100',
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    activeBg: 'bg-blue-600',
    category: 'India Specific',
    desc: 'CGHS, ECHS, Ayushman Bharat codes & rates',
    example: 'CGHS rate for cesarean section',
    templates: [
      // CGHS
      'CGHS: Cesarean section package rate 2024',
      'CGHS: Normal delivery package rate',
      'CGHS: Laparoscopic hysterectomy rate',
      'CGHS: Diagnostic laparoscopy rate',
      'CGHS: Room rent limits by city',
      // ECHS
      'ECHS: Gynec surgery rates list',
      'ECHS: Pre-authorization requirements',
      // Ayushman Bharat
      'Ayushman Bharat: C-section package rate',
      'Ayushman Bharat: Hysterectomy packages',
      'Ayushman Bharat: High risk pregnancy package',
      'Ayushman Bharat: Eligible procedures OBGYN',
      // Private Insurance
      'Private Insurance: Maternity waiting period',
      'Private Insurance: Infertility coverage',
      'Private Insurance: Claim documentation',
      'TPA: Pre-authorization format',
      'TPA: Required documents checklist',
    ],
  },
  {
    id: 'nabh-docs',
    label: 'NABH Documentation',
    icon: ClipboardPlus,
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-100',
    dot: 'bg-purple-500',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    activeBg: 'bg-purple-600',
    category: 'India Specific',
    desc: 'NABH forms, SOPs & quality indicators',
    example: 'NABH consent form for surgery',
    templates: [
      // Mandatory Forms
      'NABH Form: General consent for treatment',
      'NABH Form: Informed surgical consent',
      'NABH Form: High risk consent',
      'NABH Form: Blood transfusion consent',
      'NABH Form: Against medical advice discharge',
      'NABH Form: Patient rights document',
      // SOPs
      'NABH SOP: Labor room admission protocol',
      'NABH SOP: Emergency cesarean protocol',
      'NABH SOP: PPH management protocol',
      'NABH SOP: Surgical safety checklist',
      'NABH SOP: Infection control OT',
      // Quality Indicators
      'NABH QI: Cesarean section rate',
      'NABH QI: Maternal mortality review',
      'NABH QI: Surgical site infection rate',
      'NABH Audit: Consent documentation',
    ],
  },
  {
    id: 'govt-schemes',
    label: 'Govt Schemes',
    icon: Landmark,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-100',
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    activeBg: 'bg-amber-600',
    category: 'India Specific',
    desc: 'JSY, PMSMA, LaQshya & maternal schemes',
    example: 'JSY eligibility and benefits',
    templates: [
      // JSY
      'JSY: Eligibility criteria rural vs urban',
      'JSY: Cash benefit amounts by state',
      'JSY: ASHA incentive structure',
      // JSSK
      'JSSK: Free entitlements pregnant women',
      'JSSK: Cashless delivery services',
      // PMSMA
      'PMSMA: 9th of month protocol',
      'PMSMA: High risk identification stickers',
      // LaQshya
      'LaQshya: Labor room quality standards',
      'LaQshya: Certification requirements',
      // PMMVY
      'PMMVY: Rs 5000 benefit eligibility',
      'PMMVY: Required documents',
      // State Schemes
      'Tamil Nadu: Dr Muthulakshmi Benefit',
      'Maharashtra: MAHER scheme',
      'Karnataka: Madilu Kit',
      'Telangana: KCR Kit scheme',
    ],
  },
  {
    id: 'voice-casesheet',
    label: 'Voice Case Sheet',
    icon: Languages,
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    border: 'border-indigo-100',
    dot: 'bg-indigo-500',
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    activeBg: 'bg-indigo-600',
    category: 'India Specific',
    desc: 'Dictate Hindi/Regional → English notes',
    example: 'Convert Hindi dictation to SOAP notes',
    templates: [
      // Hindi to English
      'Voice Hindi: OPD to SOAP notes',
      'Voice Hindi: ANC checkup to case sheet',
      'Voice Hindi: Labor admission notes',
      'Voice Hindi: Post-op round notes',
      'Voice Hindi: Discharge summary',
      'Voice Hindi: Operation notes',
      // Regional
      'Voice Marathi: OPD notes to English',
      'Voice Tamil: Clinical notes',
      'Voice Telugu: Case history',
      'Voice Gujarati: Patient notes',
      // Templates
      'Template: OPD case sheet format',
      'Template: ANC card format',
      'Template: Operative notes format',
      'Template: Discharge summary format',
    ],
  },
  {
    id: 'viksit-bharat-2047',
    label: 'Viksit Bharat 2047',
    icon: Rocket,
    bg: 'bg-gradient-to-r from-orange-50 to-green-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    activeBg: 'bg-orange-600',
    category: 'India Specific',
    desc: 'Imagine gynaecology practice in India 2047 - Viksit Bharat vision',
    example: 'How will OBGYN practice look in 2047 India',
    templates: [
      // Vision 2047
      '2047 Vision: AI-assisted delivery rooms in every PHC',
      '2047 Vision: Zero maternal mortality - how we achieve it',
      '2047 Vision: Universal access to IVF in government hospitals',
      '2047 Vision: Robotic surgery in district hospitals',
      '2047 Vision: AI diagnostics replacing manual screening',
      '2047 Vision: Telemedicine connecting rural India to specialists',
      '2047 Vision: Drone delivery of emergency obstetric supplies',
      '2047 Vision: Wearable devices monitoring every pregnancy',
      '2047 Vision: Genomic medicine personalizing treatment',
      '2047 Vision: 3D printing of surgical implants on demand',
      // Technology Predictions
      'Tech 2047: AI doing 90% of ultrasound interpretations',
      'Tech 2047: Liquid biopsy replacing invasive procedures',
      'Tech 2047: Nanobots for targeted drug delivery in cancer',
      'Tech 2047: Artificial wombs for extreme preterm babies',
      'Tech 2047: Gene editing eliminating hereditary diseases',
      'Tech 2047: Brain-computer interface for paralyzed patients',
      'Tech 2047: Holographic surgery training',
      'Tech 2047: AI predicting complications before they occur',
      // Infrastructure Dreams
      'Infrastructure 2047: Every taluka with IVF center',
      'Infrastructure 2047: Blood bank in every block',
      'Infrastructure 2047: NICU in every district hospital',
      'Infrastructure 2047: Ambulances with mini-OT capability',
      'Infrastructure 2047: Solar powered rural health centers',
      // Workforce Transformation
      'Workforce 2047: AI assistants for every gynaecologist',
      'Workforce 2047: Nurses performing AI-guided procedures',
      'Workforce 2047: Virtual reality medical education',
      'Workforce 2047: Global collaboration platforms',
      // Patient Experience
      'Patient 2047: Multilingual AI health assistants',
      'Patient 2047: Cashless universal health coverage',
      'Patient 2047: Home-based pregnancy monitoring',
      'Patient 2047: Personalized fertility prediction at birth',
    ],
  },
];

// =============================================
// CATEGORY 9: AI TOOLS & MEDIA
// =============================================
const AI_TOOLS_MODES = [
  {
    id: 'ai-content',
    label: 'AI Content Studio',
    icon: Sparkles,
    bg: 'bg-gradient-to-r from-purple-50 to-pink-50',
    text: 'text-purple-700',
    border: 'border-purple-100',
    dot: 'bg-purple-500',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    activeBg: 'bg-purple-600',
    category: 'AI Tools',
    desc: 'Infographics, reels & social content',
    example: 'Create PCOS infographic for Instagram',
    templates: [
      'Create: PCOS infographic for patients',
      'Create: Pregnancy timeline visual',
      'Create: Menopause symptoms checklist',
      'Create: IVF process flowchart',
      'Create: Contraception comparison chart',
    ],
  },
  {
    id: 'image-gen',
    label: 'Image Generation',
    icon: ImageIcon,
    bg: 'bg-gradient-to-r from-blue-50 to-indigo-50',
    text: 'text-blue-700',
    border: 'border-blue-100',
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    activeBg: 'bg-blue-600',
    category: 'AI Tools',
    desc: 'AI image generation for content',
    example: 'Generate image for womens day post',
    templates: [
      'Generate: Medical illustration for patient education',
      'Generate: Social media banner for clinic',
      'Generate: Infographic on pregnancy stages',
      'Generate: Awareness poster for cervical cancer',
    ],
  },
  {
    id: 'video-gen',
    label: 'Video Generation',
    icon: Film,
    bg: 'bg-gradient-to-r from-red-50 to-orange-50',
    text: 'text-red-700',
    border: 'border-red-100',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 border-red-200',
    activeBg: 'bg-red-600',
    category: 'AI Tools',
    desc: 'AI video generation for social media',
    example: 'Generate video for PCOS awareness',
    templates: [
      'Generate: Short video on breastfeeding tips',
      'Generate: Animated explainer for IVF process',
      'Generate: Patient education video on menopause',
    ],
  },
  {
    id: 'templates-1000',
    label: '1000+ Templates',
    icon: LayoutTemplate,
    bg: 'bg-gradient-to-r from-raspberry-50 to-pink-50',
    text: 'text-raspberry-700',
    border: 'border-raspberry-100',
    dot: 'bg-raspberry-500',
    badge: 'bg-raspberry-100 text-raspberry-800 border-raspberry-200',
    activeBg: 'bg-raspberry-600',
    category: 'AI Tools',
    desc: `${TEMPLATE_COUNT}+ ready templates for every scenario`,
    example: 'Ask any clinical question',
    templates: FLAT_TEMPLATES,
  },
];

// =============================================
// COMBINE ALL MODES IN ORGANIZED ORDER
// =============================================
export const MODES = [
  ...EMERGENCY_MODES,
  ...CLINICAL_MODES,
  ...SPECIALTY_MODES,
  ...SURGERY_MODES,
  ...EDUCATION_MODES,
  ...WELLNESS_MODES,
  ...PRACTICE_MODES,
  ...INDIA_MODES,
  ...AI_TOOLS_MODES,
];

// =============================================
// GENERAL MODE (Default)
// =============================================
export const GENERAL_MODE = {
  id: 'general',
  label: 'General',
  icon: Zap,
  bg: 'bg-raspberry-50',
  text: 'text-raspberry-800',
  border: 'border-raspberry-100',
  dot: 'bg-raspberry-800',
  badge: 'bg-pink-100 text-pink-900 border-pink-200',
  activeBg: 'bg-raspberry-800',
  category: 'General',
  desc: 'General clinical query',
  example: '',
};

export const ALL_MODES = [GENERAL_MODE, ...MODES];

export const getModeByLabel = (label) =>
  ALL_MODES.find(m => m.label === label) || GENERAL_MODE;

// Mode Categories for sidebar grouping
export const MODE_CATEGORIES = [
  { name: 'Emergency & Critical Care', modes: EMERGENCY_MODES },
  { name: 'Clinical Consultation', modes: CLINICAL_MODES },
  { name: 'Specialty Areas', modes: SPECIALTY_MODES },
  { name: 'Surgery & Documentation', modes: SURGERY_MODES },
  { name: 'Education & Resources', modes: EDUCATION_MODES },
  { name: 'Wellness & Lifestyle', modes: WELLNESS_MODES },
  { name: 'Practice Management', modes: PRACTICE_MODES },
  { name: 'India Specific', modes: INDIA_MODES },
  { name: 'AI Tools', modes: AI_TOOLS_MODES },
];

// =============================================
// LANGUAGES
// =============================================
export const LANGUAGES = [
  "English", "Hindi", "Konkani", "Spanish", "French", "Arabic", "Bengali", "Portuguese",
  "Russian", "Urdu", "Indonesian", "German", "Japanese", "Marathi", "Telugu",
  "Turkish", "Tamil", "Gujarati", "Kannada", "Malayalam", "Punjabi",
  "Chinese (Simplified)", "Chinese (Traditional)", "Korean", "Italian",
  "Vietnamese", "Thai", "Polish", "Dutch", "Ukrainian", "Romanian",
  "Czech", "Swedish", "Hungarian", "Greek", "Hebrew", "Danish", "Finnish",
  "Norwegian", "Swahili", "Afrikaans", "Malay", "Tagalog", "Persian",
  "Nepali", "Sinhala", "Burmese", "Amharic", "Hausa", "Yoruba", "Zulu",
  "Somali", "Pashto", "Uzbek", "Kazakh", "Azerbaijani", "Georgian",
  "Armenian", "Catalan", "Serbian", "Croatian", "Bulgarian", "Slovak",
  "Slovenian", "Lithuanian", "Latvian", "Estonian",
];

// =============================================
// CALCULATOR TEMPLATES
// =============================================
export const CALCULATOR_TEMPLATES = [
  { label: 'EDD Calculator', query: 'Calculate EDD from LMP [enter date]', category: 'Pregnancy' },
  { label: 'Gestational Age', query: 'Calculate gestational age from LMP [enter date]', category: 'Pregnancy' },
  { label: 'Bishop Score', query: 'Bishop score: cervix [cm], [%] effaced, station [-3 to +3], consistency [firm/medium/soft], position [posterior/mid/anterior]', category: 'Labor' },
  { label: 'Fetal Weight', query: 'Fetal weight estimation: BPD [mm], AC [mm], FL [mm]', category: 'Pregnancy' },
  { label: 'BMI & Fertility', query: 'BMI calculation: weight [kg], height [cm] - fertility implications', category: 'General' },
  { label: 'AMH Interpretation', query: 'AMH interpretation: [value] ng/mL, age [years] - fertility prognosis', category: 'Fertility' },
  { label: 'AFC Assessment', query: 'AFC interpretation: [number] antral follicles, age [years] - ovarian reserve', category: 'Fertility' },
  { label: 'IVF Success Rate', query: 'IVF success probability: age [years], AMH [value], AFC [number], BMI [value]', category: 'Fertility' },
  { label: 'APGAR Score', query: 'APGAR score interpretation: 1min=[score], 5min=[score]', category: 'Neonatal' },
  { label: 'Weight Gain (Pregnancy)', query: 'Pregnancy weight gain calculator: pre-pregnancy BMI [value], current [weeks] weeks', category: 'Pregnancy' },
  { label: 'GDM Risk', query: 'GDM risk calculator: age [years], BMI [value], family history [yes/no]', category: 'Pregnancy' },
  { label: 'Ovulation Date', query: 'Ovulation date calculator: LMP [date], cycle length [days]', category: 'Fertility' },
  { label: 'Due Date by USG', query: 'Calculate due date from ultrasound: CRL [mm] at [weeks] weeks', category: 'Pregnancy' },
  { label: 'Fluid Requirement', query: 'IV fluid calculation for [weight] kg patient post surgery', category: 'Surgery' },
];
